import { APIStatus } from '@application/common/enums/api-status';
import { getCurrentUserFromJwt } from '@application/common/helpers/jwt-helper';
import LocalStorageService from '@infrastructure/local-storage-service';
import AdminSession from '@infrastructure/admin-session';
import { createSlice } from '@reduxjs/toolkit';
import { checkShuftiProStatusAsync, confirmEmailAsync, forgotPasswordAsync, loginAsync, refreshTokenAsync, regenerateEmailLinkAsync, resetPasswordAsync, signUpAsync, verifyTokenAsync } from './authThunks';
const initialState = {
    status: APIStatus.IDLE,
    currentUser: getCurrentUserFromJwt()
};
export const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        logout(state) {
            state.currentUser = undefined;
            // Ends the strong admin session server side too (cookie); never blocks the local sign-out.
            void AdminSession.logout();
            LocalStorageService.removeAccessToken();
            LocalStorageService.removeRefreshToken();
            LocalStorageService.removeReferenceNumber();
            state.apiError = undefined;
            state.status = APIStatus.IDLE;
            state.currentUserDetail = undefined;
            state.isShuftiProVerified = undefined;
        },
        resetAuthApiState(state) {
            state.status = APIStatus.IDLE;
        },
        // Strong admin session (passkey or password+TOTP): the token stays in memory, never in localStorage.
        adminSessionStarted(state, action) {
            state.status = APIStatus.IDLE;
            state.apiError = undefined;
            LocalStorageService.removeAccessToken();
            LocalStorageService.removeRefreshToken();
            state.currentUser = getCurrentUserFromJwt(action.payload);
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(loginAsync.pending, (state) => {
            state.status = APIStatus.PENDING;
            state.apiError = undefined;
        })
            .addCase(loginAsync.fulfilled, (state, action) => {
            state.status = APIStatus.IDLE;
            // Admin accounts with a passkey/TOTP get no token from the password alone.
            if (!action.payload.accessToken)
                return;
            state.currentUser = getCurrentUserFromJwt(action.payload.accessToken);
            LocalStorageService.setAccessToken(action.payload.accessToken);
            LocalStorageService.setRefreshToken(action.payload.refreshToken);
        })
            .addCase(loginAsync.rejected, (state, action) => {
            state.status = APIStatus.FAILED;
            state.apiError = action.payload;
        });
        builder
            .addCase(checkShuftiProStatusAsync.pending, (state) => {
            state.status = APIStatus.PENDING;
            state.apiError = undefined;
        })
            .addCase(checkShuftiProStatusAsync.fulfilled, (state, action) => {
            state.isShuftiProVerified = action.payload;
        })
            .addCase(checkShuftiProStatusAsync.rejected, (state, action) => {
            state.status = APIStatus.FAILED;
            state.apiError = action.payload;
        });
        builder
            .addCase(regenerateEmailLinkAsync.pending, (state) => {
            state.status = APIStatus.PENDING;
            state.apiError = undefined;
        })
            .addCase(regenerateEmailLinkAsync.fulfilled, (state, action) => {
            state.status = APIStatus.SUCCESS;
        })
            .addCase(regenerateEmailLinkAsync.rejected, (state, action) => {
            state.status = APIStatus.FAILED;
            state.apiError = action.payload;
        });
        builder
            .addCase(refreshTokenAsync.pending, (state) => {
            state.status = APIStatus.PENDING;
            state.apiError = undefined;
        })
            .addCase(refreshTokenAsync.fulfilled, (state, action) => {
            state.status = APIStatus.IDLE;
            state.currentUser = getCurrentUserFromJwt(action.payload.accessToken);
            if (action.payload.strongSession)
                return;
            LocalStorageService.setAccessToken(action.payload.accessToken);
            LocalStorageService.setRefreshToken(action.payload.refreshToken);
        })
            .addCase(refreshTokenAsync.rejected, (state, action) => {
            state.status = APIStatus.FAILED;
        });
        builder
            .addCase(signUpAsync.pending, (state) => {
            state.status = APIStatus.PENDING;
            state.apiError = undefined;
        })
            .addCase(signUpAsync.fulfilled, (state) => {
            state.status = APIStatus.SUCCESS;
        })
            .addCase(signUpAsync.rejected, (state, action) => {
            state.status = APIStatus.FAILED;
            state.apiError = action.payload;
        });
        builder
            .addCase(confirmEmailAsync.pending, (state) => {
            state.status = APIStatus.PENDING;
            state.apiError = undefined;
        })
            .addCase(confirmEmailAsync.fulfilled, (state) => {
            state.status = APIStatus.SUCCESS;
        })
            .addCase(confirmEmailAsync.rejected, (state, action) => {
            state.status = APIStatus.FAILED;
            state.apiError = action.payload;
        });
        builder
            .addCase(forgotPasswordAsync.pending, (state) => {
            state.status = APIStatus.PENDING;
            state.apiError = undefined;
        })
            .addCase(forgotPasswordAsync.fulfilled, (state) => {
            state.status = APIStatus.SUCCESS;
        })
            .addCase(forgotPasswordAsync.rejected, (state, action) => {
            state.status = APIStatus.FAILED;
            state.apiError = action.payload;
        });
        builder
            .addCase(resetPasswordAsync.pending, (state) => {
            state.status = APIStatus.PENDING;
            state.apiError = undefined;
        })
            .addCase(resetPasswordAsync.fulfilled, (state) => {
            state.status = APIStatus.SUCCESS;
        })
            .addCase(resetPasswordAsync.rejected, (state, action) => {
            state.status = APIStatus.FAILED;
            state.apiError = action.payload;
        });
        builder
            .addCase(verifyTokenAsync.pending, (state) => {
            state.status = APIStatus.PENDING;
            state.apiError = undefined;
        })
            .addCase(verifyTokenAsync.fulfilled, (state) => {
            state.status = APIStatus.SUCCESS;
        })
            .addCase(verifyTokenAsync.rejected, (state, action) => {
            state.status = APIStatus.FAILED;
            state.apiError = action.payload;
        });
    }
});
export const { logout, resetAuthApiState, adminSessionStarted } = authSlice.actions;
export default authSlice.reducer;
